import { Button, Card, Form, Input, Modal, Popconfirm, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type FeeStructure = {
  id: string;
  code: string;
  name: string;
  session_name?: string;
  session_code?: string;
  is_active?: number | boolean;
  effective_on?: string | null;
  line_count?: number;
};

type CreateForm = {
  session_id: string;
  code: string;
  name: string;
  effective_on?: string;
};

export default function FeeStructuresPage() {
  const { t } = useTranslation('education');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.fee.manage') || user?.permissions?.includes('education.admin')
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm<CreateForm>();

  const list = useQuery({
    queryKey: ['education-fee-structures'],
    queryFn: () => api.get<FeeStructure[]>('/education/fees/structures'),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['education-fee-structures'] });

  const create = useMutation({
    mutationFn: (values: CreateForm) =>
      api.post<{ structure: { id: string } }>('/education/fees/structures', {
        session_id: values.session_id,
        code: values.code,
        name: values.name,
        effective_on: values.effective_on || null,
        lines: [],
      }),
    onSuccess: (result) => {
      message.success(t('feeStructureCreated'));
      setCreateOpen(false);
      createForm.resetFields();
      refresh();
      const newId = (result.data as { structure?: { id?: string } })?.structure?.id;
      if (newId) navigate(`/education/fees/structures/${newId}`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const remove = useMutation({
    mutationFn: (structureId: string) => api.delete(`/education/fees/structures/${structureId}`),
    onSuccess: () => {
      message.success(t('feeStructureDeleted'));
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('feeStructures')}
      description={t('feeStructuresIntro')}
      extra={
        <Space>
          {canManage ? (
            <Button type="primary" onClick={() => setCreateOpen(true)}>
              {t('newFeeStructure')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/fees')}>{t('backToFees')}</Button>
        </Space>
      }
    >
      <Card size="small">
        <Table
          rowKey="id"
          size="small"
          loading={list.isLoading}
          dataSource={list.data?.data ?? []}
          pagination={{ pageSize: 25 }}
          columns={[
            { title: t('code'), dataIndex: 'code', width: 140 },
            { title: t('name'), dataIndex: 'name' },
            {
              title: t('session'),
              render: (_: unknown, row: FeeStructure) => row.session_name || row.session_code || '—',
            },
            {
              title: t('feeStructureLines'),
              dataIndex: 'line_count',
              width: 100,
              render: (v: number | undefined) => v ?? '—',
            },
            {
              title: t('status'),
              dataIndex: 'is_active',
              width: 100,
              render: (v) =>
                v === 0 || v === false ? <Tag>{t('inactive')}</Tag> : <Tag color="success">{t('active')}</Tag>,
            },
            {
              title: t('actions'),
              width: 200,
              render: (_: unknown, row: FeeStructure) => (
                <Space>
                  <Button size="small" type="primary" onClick={() => navigate(`/education/fees/structures/${row.id}`)}>
                    {t('open')}
                  </Button>
                  {canManage ? (
                    <Popconfirm
                      title={t('feeStructureDeleteConfirm')}
                      onConfirm={() => remove.mutate(row.id)}
                    >
                      <Button size="small" danger loading={remove.isPending}>
                        {tc('delete')}
                      </Button>
                    </Popconfirm>
                  ) : null}
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={t('newFeeStructure')}
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        footer={null}
        destroyOnClose
        width={520}
      >
        <Form form={createForm} layout="vertical" onFinish={(values) => create.mutate(values)}>
          <Form.Item name="session_id" label={t('session')} rules={[{ required: true }]}>
            <EducationSessionSelect />
          </Form.Item>
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="effective_on" label={t('effectiveOn')}>
            <Input placeholder="YYYY-MM-DD" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>
    </EducationPageChrome>
  );
}
